import type { ReactNode } from 'react'

type OnboardingLayoutProps = {
  children: ReactNode
  step?: number
  totalSteps?: number
  eyebrow?: string
  title?: string
  subtitle?: string
}

function StepIndicator({ step, totalSteps }: { step: number; totalSteps: number }) {
  return (
    <div className="mb-8 flex items-center gap-2">
      {Array.from({ length: totalSteps }).map((_, idx) => {
        const isActive = idx < step
        return (
          <span
            key={idx}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              isActive ? 'w-8 bg-indigo-600' : 'w-4 bg-line'
            }`}
          />
        )
      })}
      <span className="ml-2 text-xs font-medium text-ink-3">
        Step {step} of {totalSteps}
      </span>
    </div>
  )
}

type ContextualHeaderProps = {
  eyebrow?: string
  title: string
  subtitle?: string
  tone: 'dark' | 'light'
}

function ContextualHeader({ eyebrow, title, subtitle, tone }: ContextualHeaderProps) {
  const isDark = tone === 'dark'
  return (
    <div>
      {eyebrow && (
        <p
          className={`mb-3 text-sm font-semibold uppercase tracking-wider ${
            isDark ? 'text-indigo-300' : 'text-indigo-600'
          }`}
        >
          {eyebrow}
        </p>
      )}
      <h1
        className={
          isDark
            ? 'text-4xl font-bold leading-[1.15] tracking-tight text-white lg:text-[44px]'
            : 'text-3xl font-extrabold tracking-tight text-ink'
        }
      >
        {title}
      </h1>
      {subtitle && (
        <p
          className={
            isDark ? 'mt-5 text-[17px] leading-relaxed text-slate-300' : 'mt-3 text-[15px] text-ink-3'
          }
        >
          {subtitle}
        </p>
      )}
    </div>
  )
}

function BrandPitch() {
  return (
    <div className="max-w-[420px]">
      <h2 className="mb-6 text-4xl font-bold leading-[1.15] tracking-tight lg:text-[44px]">
        Smart Security Management
      </h2>
      <p className="text-[17px] font-normal leading-relaxed text-slate-300">
        Smart deployments. Safer sites. Stronger control.
      </p>
    </div>
  )
}

export function OnboardingLayout({
  children,
  step,
  totalSteps,
  eyebrow,
  title,
  subtitle,
}: OnboardingLayoutProps) {
  const showStepper = typeof step === 'number' && typeof totalSteps === 'number'
  const hasHeader = typeof title === 'string'

  return (
    <div className="flex min-h-screen w-full font-sans">
      <div className="relative hidden flex-1 flex-col overflow-hidden bg-slate-900 p-12 text-white lg:flex">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
        <div className="absolute left-0 right-0 top-0 -z-10 m-auto h-[310px] w-[310px] rounded-full bg-indigo-500 opacity-20 blur-[100px]" />

        <div className="relative z-10 flex h-full flex-col">
          <div className="flex items-center gap-4">
            <div className="rounded-xl border-0 bg-white p-2.5 shadow-md">
              <img src="/logo-mark.svg" alt="NezTrack Logo" className="h-9 w-auto" />
            </div>
            <span className="text-3xl font-extrabold tracking-tight text-white drop-shadow-sm">
              NezTrack
            </span>
          </div>

          <div className="mb-auto mt-auto max-w-[440px]">
            {hasHeader ? (
              <ContextualHeader
                eyebrow={eyebrow}
                title={title as string}
                subtitle={subtitle}
                tone="dark"
              />
            ) : (
              <BrandPitch />
            )}
          </div>

          <div className="mt-auto text-sm font-medium text-slate-400">
            &copy; {new Date().getFullYear()} NezTrack. All rights reserved.
          </div>
        </div>
      </div>

      <main className="relative flex flex-1 flex-col justify-center bg-surface p-6 lg:p-12 xl:p-16">
        <div className="mx-auto w-full max-w-lg">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="rounded-xl border border-line bg-hover p-2">
              <img src="/logo-mark.svg" alt="NezTrack Logo" className="h-8 w-auto" />
            </div>
            <span className="text-2xl font-bold tracking-tight text-ink">NezTrack</span>
          </div>

          {showStepper && <StepIndicator step={step} totalSteps={totalSteps} />}

          {hasHeader && (
            <div className="mb-6 lg:hidden">
              <ContextualHeader
                eyebrow={eyebrow}
                title={title as string}
                subtitle={subtitle}
                tone="light"
              />
            </div>
          )}

          {!hasHeader && eyebrow && (
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-indigo-600">
              {eyebrow}
            </p>
          )}

          {children}
        </div>
      </main>
    </div>
  )
}
